import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Offcanvas, OffcanvasHeader, OffcanvasBody, FormGroup, Label, Input, Button, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTrash, faTimes, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import { useColorPalette } from '../../hooks/useColorPalette';
import axios from 'axios';
import { getNodeApiUrl } from '../../config/api.config';
import { toast } from 'react-toastify';
import { selectAccountData } from '../../redux/main/accountSlice';
import EmployeeSelect from '../EmployeeSelect';



/**
 * Componente per l'aggiunta/modifica delle assenze dei dipendenti
 */
const AbsenceOffcanvas = ({
  isOpen,
  onClose,
  absenceData = null,
  initialDate = null, // Data iniziale preimpostata (da click su giorno del calendario)
  onSuccess,
  absenceTypes = []
}) => {
  const colors = useColorPalette();
  const accountData = useSelector(selectAccountData);
  const accountId = accountData?._id;

  // Stati del form
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedAbsenceType, setSelectedAbsenceType] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('17:00');
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Gestione dipendenti - usa EmployeeSelect component

  // Stato per modale di conferma eliminazione
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // Gestione validazione
  const [errors, setErrors] = useState({});

  // Determina se è in modalità modifica
  const isEditMode = absenceData !== null;


  // Imposta i dati iniziali quando il componente viene aperto
  useEffect(() => {
    // Reset errori quando il componente viene aperto
    if (isOpen) {
      setErrors({});
      // AbsenceOffcanvas aperto
      
      if (isEditMode && absenceData) {
        // In modalità modifica, carica i dati dell'assenza esistente
        // Otteniamo il valore di timezoneDiff dal database o usiamo l'offset locale come fallback
        const timezoneDiff = absenceData.timezoneDiff || new Date().getTimezoneOffset() * -1;
        
        // Verifica e log dei dati ricevuti
        console.log('📆 Dati assenza ricevuti:', {
          start: absenceData.start,
          end: absenceData.end,
          tipo: typeof absenceData.start,
          timezoneDiff: timezoneDiff
        });
        
        // Assicurarsi che abbiamo date valide
        let startDateTime, endDateTime;
        try {
          // Se le date sono stringhe ISO, creiamo oggetti Date
          if (typeof absenceData.start === 'string') {
            startDateTime = new Date(absenceData.start);
          } else if (absenceData.start instanceof Date) {
            // Se è già un oggetto Date, lo usiamo direttamente
            startDateTime = absenceData.start;
          } else {
            throw new Error('Formato data di inizio non valido');
          }
          
          if (typeof absenceData.end === 'string') {
            endDateTime = new Date(absenceData.end);
          } else if (absenceData.end instanceof Date) {
            endDateTime = absenceData.end;
          } else {
            // In caso di data di fine non valida, usa la data di inizio + 8 ore
            console.warn('⚠️ Data di fine non valida, uso data inizio + 8 ore');
            endDateTime = new Date(startDateTime);
            endDateTime.setHours(endDateTime.getHours() + 8);
          }
          
          console.log('🕒 Date convertite:', {
            startDateTime: startDateTime,
            startTimestamp: startDateTime.getTime(),
            endDateTime: endDateTime,
            endTimestamp: endDateTime.getTime(),
            timezoneDiff: timezoneDiff
          });
          
        } catch (error) {
          console.error('🚨 Errore nella conversione delle date:', error);
          // Fallback con la data corrente
          startDateTime = new Date();
          startDateTime.setHours(9, 0, 0, 0);
          endDateTime = new Date();
          endDateTime.setHours(17, 0, 0, 0);
        }
        
        // Formatta la data in YYYY-MM-DD
        const formatDate = (date) => {
          return date.toISOString().split('T')[0];
        };

        // Formatta l'ora in HH:MM, usando l'ora UTC + offset per ottenere l'ora corretta
        const formatTime = (date) => {
          // Usiamo getUTCHours e aggiungiamo l'offset in ore (timezoneDiff è in minuti)
          const hours = (date.getUTCHours() + Math.floor(timezoneDiff / 60)) % 24;
          const minutes = (date.getUTCMinutes() + (timezoneDiff % 60)) % 60;
          return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        };

        // Imposta i valori nel form
        setStartDate(formatDate(startDateTime));
        setStartTime(formatTime(startDateTime));
        setEndDate(formatDate(endDateTime));
        setEndTime(formatTime(endDateTime));
        setNotes(absenceData.notes || '');

        // Imposta il dipendente selezionato
        if (absenceData.userId) {
          setSelectedEmployee({
            value: absenceData.userId,
            label: absenceData.userLabel || 'Dipendente'
          });
        }

        // Imposta il tipo di assenza
        if (absenceData.absenceType) {
          // Trova il tipo di assenza corrispondente nell'array dei tipi
          const absenceType = absenceTypes.find(type => 
            // Priorità all'_id esatto, poi al codice/testo
            type._id === absenceData.absenceTypeId || 
            type.id === absenceData.absenceTypeId || 
            type.text === absenceData.absenceType
          );
          
          // Ricerca tipo assenza
          
          if (absenceType) {
            setSelectedAbsenceType({
              value: absenceType.id,
              label: absenceType.text,
              _id: absenceType._id,
              color: absenceType.color
            });
          } else if (absenceData.absenceTypeId) {
            setSelectedAbsenceType({
              value: absenceData.absenceTypeId,
              label: absenceData.absenceType || 'Tipo assenza',
              _id: absenceData.absenceTypeId // Assegnamo direttamente l'ID
            });
          }
        }
      } else {
        // In modalità inserimento, impostiamo le date predefinite e resettiamo tutto
        // Reset valori form per nuova assenza
        // Se c'è una data iniziale (da click su giorno), usala; altrimenti usa oggi
        const dateToUse = initialDate ? new Date(initialDate) : new Date();
        const formattedDate = dateToUse.toISOString().split('T')[0];
        
        console.log('📅 Data iniziale per nuova assenza:', initialDate, '-> formattata:', formattedDate);
        
        setStartDate(formattedDate);
        setEndDate(formattedDate);
        setStartTime('09:00');
        setEndTime('17:00');
        setNotes('');
        setSelectedEmployee(null);
        setSelectedAbsenceType(null);
      }
    }
  }, [isOpen, isEditMode, absenceData, absenceTypes, initialDate]);

  // Validazione del form
  const validateForm = () => {
    const newErrors = {};
    
    // Controlla che il dipendente sia selezionato
    if (!selectedEmployee) {
      newErrors.employee = 'Selezionare un dipendente';
    }

    // Controlla che il tipo di assenza sia selezionato
    if (!selectedAbsenceType) {
      newErrors.absenceType = 'Selezionare un tipo di assenza';
    }

    // Controlla che le date siano valide
    if (!startDate) {
      newErrors.startDate = 'Inserire una data di inizio';
    }
    
    if (!endDate) {
      newErrors.endDate = 'Inserire una data di fine';
    }
    
    // Controlla che data/ora di inizio non sia dopo data/ora di fine
    const startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(`${endDate}T${endTime}`);
    
    if (startDateTime > endDateTime) {
      newErrors.dateRange = 'La data/ora di inizio non può essere successiva alla data/ora di fine';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Funzione per salvare l'assenza
  const handleSave = async () => {
    // Tentativo di salvare l'assenza
    if (!validateForm()) {
      // Validazione form fallita
      return;
    }
    
    // Validazione form completata
    setLoading(true);

    try {
      // Ottieni l'absenceTypeId dal tipo di assenza selezionato
      // Verifichiamo se è disponibile nelle impostazioni dell'account
      const absenceType = accountData?.settings?.employeeAbsenceTypes?.find(type => 
        type.code === selectedAbsenceType.value || type.label === selectedAbsenceType.label
      );

      // Utilizziamo l'_id dell'oggetto originale nell'array accountData.settings.employeeAbsenceTypes
      const absenceTypeId = absenceType?._id || selectedAbsenceType._id;
      
      // Tipo assenza trovato nell'account
      // ID tipo assenza utilizzato
      
      // Calcoliamo l'offset del fuso orario locale in minuti
      const timezoneOffset = new Date().getTimezoneOffset() * -1;
      // Offset fuso orario locale (minuti)
      
      // Costruisci l'oggetto assenza
      const absencePayload = {
        userId: selectedEmployee.value,
        userLabel: selectedEmployee.label,
        absenceType: selectedAbsenceType.value, // Il codice del tipo di assenza (es. 'ferie', 'malattia')
        absenceTypeId: absenceTypeId, // ID MongoDB del tipo di assenza
        start: new Date(`${startDate}T${startTime}`).toISOString(),
        end: new Date(`${endDate}T${endTime}`).toISOString(),
        timezoneDiff: timezoneOffset, // Aggiungiamo l'offset del fuso orario
        notes: notes || '',
        files: files || [],
        accountId: accountId
      };
      
      // Dettagli tipo assenza selezionato
      
      // Payload inviato

      const token = localStorage.getItem('installamelo-auth-token');
      if (!token) {
        throw new Error('Token di autenticazione non disponibile');
      }

      let response;
      
      // Determina se è un'operazione di aggiunta o modifica
      if (isEditMode) {
        // Aggiorna l'assenza esistente
        const apiUrl = getNodeApiUrl(`/employeeAbsences/${absenceData._id}`);
        // PUT su API
        // Dati inviati
        response = await axios.put(
          apiUrl,
          absencePayload,
          {
            headers: {
              'Content-Type': 'application/json',
              'auth-token': token
            }
          }
        );
        
        if (response.data && response.data.success) {
          toast.success('Assenza aggiornata con successo');
          onSuccess && onSuccess();
          onClose();
        }
      } else {
        // Crea una nuova assenza
        const apiUrl = getNodeApiUrl('/employeeAbsences');
        // POST su API
        // Dati inviati
        response = await axios.post(
          apiUrl,
          absencePayload,
          {
            headers: {
              'Content-Type': 'application/json',
              'auth-token': token
            }
          }
        );
        
        // Risposta ricevuta
        if (response.data && response.data.success) {
          // Assenza aggiunta con successo
          toast.success('Assenza aggiunta con successo');
          onSuccess && onSuccess();
          onClose();
        }
      }
    } catch (error) {
      console.error('Errore durante il salvataggio dell\'assenza:', error);
      // Dettagli risposta
      
      // Mostra un messaggio di errore più dettagliato
      let errorMessage = 'Si è verificato un errore';
      if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(`Errore: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Funzione per eliminare l'assenza
  const handleDelete = async () => {
    if (!isEditMode || !absenceData?._id) {
      return;
    }
    
    setLoading(true);

    try {
      const token = localStorage.getItem('installamelo-auth-token');
      if (!token) {
        throw new Error('Token di autenticazione non disponibile');
      }

      // URL corretto: deve essere employeeAbsences (senza il trattino)
      const response = await axios.delete(
        getNodeApiUrl(`/employeeAbsences/${absenceData._id}`),
        {
          headers: {
            'Content-Type': 'application/json',
            'auth-token': token
          }
        }
      );
      
      // Richiesta eliminazione assenza inviata con successo
      
      if (response.data && response.data.success) {
        toast.success('Assenza eliminata con successo');
        setDeleteModalOpen(false);
        onSuccess && onSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Errore durante l\'eliminazione dell\'assenza:', error);
      toast.error(`Errore: ${error.response?.data?.message || error.message || 'Si è verificato un errore'}`);
      setDeleteModalOpen(false);
    } finally {
      setLoading(false);
    }
  };

  // Aggiungi stili personalizzati per i campi data/ora
  useEffect(() => {
    const styleId = 'datetime-field-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        .datetime-field {
          border: 1px solid #ced4da;
          border-radius: 4px;
          padding: 0.375rem 0.75rem;
          height: 38px;
          width: 100%;
        }
        .datetime-field:focus {
          border-color: #86b7fe;
          outline: 0;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }
        .error-feedback {
          width: 100%;
          margin-top: 0.25rem;
          font-size: 0.875em;
          color: #dc3545;
        }
      `;
      document.head.appendChild(style);
    }

    return () => {
      const styleElem = document.getElementById(styleId);
      if (styleElem) styleElem.remove();
    };
  }, []);

  return (
    <>
      <Offcanvas 
        isOpen={isOpen} 
        toggle={onClose} 
        direction="end" 
        style={{ width: '40%', minWidth: '350px', maxWidth: '90vw' }}
      >
        <OffcanvasHeader toggle={onClose}>
          {isEditMode ? 'Modifica Assenza' : 'Aggiungi Assenza'}
        </OffcanvasHeader>
        <OffcanvasBody>
          {/* Form per l'inserimento/modifica dell'assenza */}
          <Row>
            <Col md={6} className="mb-3">
              <FormGroup>
                <Label for="employeeSelect">Dipendente</Label>
                <EmployeeSelect
                  selectedOptions={selectedEmployee}
                  setSelectedOptions={setSelectedEmployee}
                  isMulti={false}
                  placeholder="Seleziona dipendente..."
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuPlacement="auto"
                />
                {errors.employee && <div className="error-feedback">{errors.employee}</div>}
              </FormGroup>
            </Col>
            <Col md={6} className="mb-3">
              <FormGroup>
                <Label for="absenceTypeSelect">Tipo Assenza</Label>
                <Select
                  id="absenceTypeSelect"
                  placeholder="Seleziona tipo assenza"
                  isClearable={true}
                  options={absenceTypes.map(type => {
                    // Tipo assenza
                    return {
                      value: type.id,
                      label: type.text,
                      color: type.color,
                      _id: type._id  // ID MongoDB del tipo di assenza
                    };
                  })}
                  value={selectedAbsenceType}
                  onChange={(selected) => {
                    // Tipo assenza selezionato
                    setSelectedAbsenceType(selected);
                  }}
                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      borderColor: '#ced4da',
                      fontSize: '0.95rem'
                    }),
                    option: (baseStyles, { data, isSelected }) => ({
                      ...baseStyles,
                      color: isSelected ? '#fff' : '#333',
                      backgroundColor: isSelected ? data.color : baseStyles.backgroundColor,
                      ':hover': {
                        ...baseStyles[':hover'],
                        backgroundColor: isSelected ? data.color : `${data.color}22`
                      },
                      ':active': {
                        ...baseStyles[':active'],
                        backgroundColor: data.color
                      }
                    })
                  }}
                  classNamePrefix="select"
                />
                {errors.absenceType && <div className="error-feedback">{errors.absenceType}</div>}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6} className="mb-3">
              <FormGroup>
                <Label for="startDate">Data Inizio</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="datetime-field"
                />
              </FormGroup>
            </Col>
            <Col md={6} className="mb-3">
              <FormGroup>
                <Label for="startTime">Ora Inizio</Label>
                <Input
                  id="startTime"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="datetime-field"
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6} className="mb-3">
              <FormGroup>
                <Label for="endDate">Data Fine</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="datetime-field"
                  min={startDate} // Imposta il minimo alla data di inizio
                />
              </FormGroup>
            </Col>
            <Col md={6} className="mb-3">
              <FormGroup>
                <Label for="endTime">Ora Fine</Label>
                <Input
                  id="endTime"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="datetime-field"
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Messaggio di errore per la validazione data/ora */}
          {errors.dateRange && (
            <div className="error-feedback mb-3">{errors.dateRange}</div>
          )}

          <FormGroup className="mb-4">
            <Label for="notes">Note</Label>
            <Input
              id="notes"
              type="textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Inserisci eventuali note..."
            />
          </FormGroup>

          {/* Pulsanti di azione */}
          <div className="d-flex justify-content-end gap-2 mt-4">
            {isEditMode ? (
              <>
                <Button 
                  color="danger" 
                  onClick={() => setDeleteModalOpen(true)}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faTrash} className="me-2" />
                  Elimina assenza
                </Button>
                <Button 
                  color="primary" 
                  onClick={handleSave}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faSave} className="me-2" />
                  {loading ? 'Salvataggio...' : 'Modifica assenza'}
                </Button>
              </>
            ) : (
              <Button 
                color="primary" 
                onClick={handleSave}
                disabled={loading}
              >
                <FontAwesomeIcon icon={faSave} className="me-2" />
                {loading ? 'Salvataggio...' : 'Aggiungi assenza'}
              </Button>
            )}
            <Button 
              color="secondary" 
              onClick={onClose}
              disabled={loading}
            >
              <FontAwesomeIcon icon={faTimes} className="me-2" />
              Chiudi
            </Button>
          </div>
        </OffcanvasBody>
      </Offcanvas>

      {/* Modal di conferma eliminazione */}
      <Modal isOpen={deleteModalOpen} toggle={() => setDeleteModalOpen(false)} centered>
        <ModalHeader toggle={() => setDeleteModalOpen(false)}>
          Conferma eliminazione
        </ModalHeader>
        <ModalBody>
          <div className="d-flex align-items-center mb-3">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning me-3" size="2x" />
            <div>
              Sei sicuro di voler eliminare questa assenza?<br />
              <small className="text-muted">Questa azione non può essere annullata.</small>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteModalOpen(false)}>
            Annulla
          </Button>
          <Button color="danger" onClick={handleDelete} disabled={loading}>
            {loading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

AbsenceOffcanvas.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  absenceData: PropTypes.object,
  initialDate: PropTypes.string, // Data iniziale in formato YYYY-MM-DD
  onSuccess: PropTypes.func,
  absenceTypes: PropTypes.array
};

export default AbsenceOffcanvas;
